import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { tryApiGet } from "./client";

// Cursor-based Prev/Next paging (phase 4 amendment — see
// lib/definitions.ts's PAGE_SIZE comment for the "1,840 findings as one
// 192,000px page" bug this fixes). The contract only ever hands back a
// forward cursor, never a `prev_cursor` — Prev is answered locally from a
// stack of cursors already visited this session instead of a second round
// trip, so going back is instant and Next is the only direction that ever
// hits the network. Changing `paramsKey` (filters) resets back to page 1,
// same as changing `path`.
//
// Generalized for the Packets page (a real gap found: two
// of nine advertised nav destinations silently rendered the Live Map).
// GET /api/packets?limit=&before= returns a BARE ARRAY with no total/
// next_cursor field at all — unlike Audit/Coverage's {..., total,
// next_cursor} object shape, and its cursor query param is named `before`,
// not `cursor`. `getNextCursor`/`getTotal`/`cursorParamName` let a caller
// adapt to a differently-shaped paged endpoint without inventing a second
// paging hook; the defaults reproduce the original object-shape behaviour
// exactly, so Audit/Coverage need no changes.
//
// 2026-09-30 data-layer slice 2: rebuilt on react-query (`useQuery` +
// `placeholderData: keepPreviousData`) instead of a bare
// useState+useEffect pair, WITHOUT changing this file's exported shape —
// every existing caller (PacketsPage, Audit's Findings/Reachability tabs,
// MeshCoreConfigAudit) gets the improved behaviour for free, no call-site
// changes needed:
//   - Paging BACK to a page already seen in this session shows it
//     INSTANTLY (react-query's own cache, keyed by cursor — no cursor is
//     ever refetched once seen unless it goes stale/is invalidated).
//   - Changing a filter (paramsKey) no longer resets `data` to null while
//     the new page loads — `keepPreviousData` keeps the OLD rows on
//     screen until the new ones arrive.
//   - `data` is `null` pre-first-ever-fetch (true loading), `"unavailable"`
//     on a genuine 404 (an endpoint this backend build doesn't have — the
//     one case that resolves rather than throws, same as before), OR
//     whatever was last successfully fetched/persisted if a DIFFERENT
//     failure (network error, blocked request, 5xx) hits a query that
//     already had good data — react-query keeps that data and leaves
//     `status`/`error` reflecting the failure instead of clobbering it
//     (see the queryFn's own comment below for a real bug this fixed). An
//     empty/error message a caller derives from `data` still only ever
//     appears once the server has genuinely answered (or a page has
//     genuinely never had data to show).
export interface Paged {
  total?: number;
  next_cursor?: string | null;
}

export interface PagedFetchOptions<T> {
  // Default: read `.next_cursor` off the response object. Packets' bare
  // array has no such field — its caller derives a cursor from the
  // array's own last item instead (its `ts`, since /api/packets?before=
  // takes a plain ISO-timestamp cursor, not a field the endpoint hands
  // back itself).
  getNextCursor?: (data: T) => string | null | undefined;
  // Default: "cursor". Packets' /api/packets?before= names it differently.
  cursorParamName?: string;
  // Persist ONLY this hook's page-1 (cursor===null) result to the shared
  // IndexedDB query cache (lib/queryClient.ts) — off by default. Set by
  // PacketsPage for its own first page (2026-09-30 slice 2: "persist the
  // Packets page's first page, only the first page") so it's there on
  // reload/offline the same way the rest of the global-at-login data is;
  // deeper pages, and every other usePagedFetch caller, stay
  // session-only, rehydrated by a plain refetch — same as before.
  persistFirstPage?: boolean;
  // Replaces `cursorParamName`: writes the cursor into the request itself, for an endpoint whose
  // cursor is more than one value (Packets: a time and a row id).
  applyCursor?: (search: URLSearchParams, cursor: string) => void;
}

export interface PagedFetchState<T> {
  data: T | null | "unavailable";
  page: number;
  hasPrev: boolean;
  hasNext: boolean;
  next: () => void;
  prev: () => void;
}

const defaultGetNextCursor = <T,>(data: T): string | null | undefined => (data as unknown as Paged)?.next_cursor;

export function usePagedFetch<T>(
  path: string,
  params: Record<string, string>,
  limit: number,
  options?: PagedFetchOptions<T>,
): PagedFetchState<T> {
  const getNextCursor = options?.getNextCursor ?? defaultGetNextCursor<T>;
  const cursorParamName = options?.cursorParamName ?? "cursor";
  const persistFirstPage = options?.persistFirstPage ?? false;
  const applyCursor = options?.applyCursor;
  const paramsKey = JSON.stringify(params);

  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);

  // Filters (or the endpoint itself) changed — start over at page 1.
  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
  }, [path, paramsKey]);

  const cursor = cursorStack[pageIndex] ?? null;

  const query = useQuery<T | "unavailable">({
    // path + filters + cursor fully identify a page's content — paging
    // back to a cursor already in this key space is served straight from
    // react-query's cache, no refetch.
    queryKey: ["pagedFetch", path, paramsKey, cursor],
    queryFn: async ({ signal }) => {
      const search = new URLSearchParams(params);
      search.set("limit", String(limit));
      if (cursor) {
        if (applyCursor) applyCursor(search, cursor);
        else search.set(cursorParamName, cursor);
      }
      // tryApiGet resolves to `null` ONLY on a 404 — a stable, genuine
      // answer ("this endpoint doesn't exist on this backend build"),
      // mapped to the "unavailable" sentinel below same as before. Any
      // OTHER failure (network error, blocked request, 5xx) is left to
      // THROW rather than being caught and resolved to "unavailable" —
      // this was a real bug found live in this exact change (2026-09-30):
      // catching every failure into a resolved value meant react-query
      // treated a failed background refetch (e.g. /api/* blocked while
      // PacketsPage's persisted first page already had real rows cached)
      // as a legitimate NEW successful result, clobbering the good
      // persisted data with the string "unavailable" — exactly the
      // data-presence bug useGlobalQuery.ts's own header comment
      // describes, reproduced here because this queryFn was swallowing
      // the error instead of letting react-query's own "keep last-good
      // data on a failed fetch" behavior do its job. Letting it throw
      // means a real failure leaves `query.data` (and thus what
      // PacketsPage renders) untouched.
      const res = await tryApiGet<T>(`${path}?${search.toString()}`, signal);
      return res ?? "unavailable";
    },
    // Keeps the PREVIOUS page/filter's rows on screen while the new
    // cursor/paramsKey's request is in flight, instead of resetting to
    // null — see this file's own header comment.
    placeholderData: keepPreviousData,
    meta: { persist: persistFirstPage && cursor === null },
  });

  function next() {
    if (!query.data || query.data === "unavailable") return;
    const nextCursor = getNextCursor(query.data);
    if (!nextCursor) return;
    setCursorStack((stack) => (pageIndex === stack.length - 1 ? [...stack, nextCursor] : stack));
    setPageIndex((i) => i + 1);
  }

  function prev() {
    setPageIndex((i) => Math.max(0, i - 1));
  }

  return {
    // query.data is `undefined` only pre-first-ever-fetch for this EXACT
    // queryKey with no placeholder available yet either (e.g. this
    // session's very first render) — mapped to `null` to match every
    // existing caller's "still loading" check.
    data: query.data ?? null,
    page: pageIndex + 1,
    hasPrev: pageIndex > 0,
    hasNext: !!(query.data && query.data !== "unavailable" && getNextCursor(query.data)),
    next,
    prev,
  };
}
